import React from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import TextTranslationEditForm from "./TextTranslationEditForm";
import TextTranslationAddStepper from "./TextTranslationAddStepper";

import { columns, defaultTableOptions } from "../../../../helper-functions/table/textTranslations";

import { createUploadObject } from "../../../../helper-functions/shared/miscUtils";
import {
    approveTextTranslations,
    createTextTranslation,
    deleteTextTranslations,
    queryTextTranslations,
    retryTextTranslations,
    updateTextTranslation
} from "../../../../helper-functions/api/translationQueries";
import { getLanguage } from "../../../../config/language/language";

/**
 * Table showing text translations.
 * If no data found, show error message instead.
 */
export default function TextTranslationTable(props) {
    const textTranslations = props.backend.textTranslations;

    const showCreateDialog = () => {
        props.showDialog({
            title: "Add new text translation",
            component: <TextTranslationAddStepper {...props} callback={(confirm, options) => handleCreate(confirm, options)} />
        });
    };

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete text translation(s)",
            message: `Are you sure you want to delete the ${textTranslations.selected.length} selected text translation(s)?

            Any related audio translations will also be deleted.`,
            actionTitle: "Delete",
            actionColor: "error",
            callback: (confirm) => handleDelete(confirm)
        });
    };

    const showUpdateDialog = () => {
        props.showDialog({
            title: "Edit text translation",
            message: "Click 'Update' to confirm changes.",
            component: <TextTranslationEditForm {...props} row={textTranslations.selected?.[0]} callback={(confirm, options) => handleUpdate(confirm, options)} />
        });
    };

    const showApproveDialog = () => {
        props.showDialog({
            title: "Approve text translation(s)",
            message: `Are you sure you want to approve the ${textTranslations.selected.length} selected text translation(s)?`,
            actionTitle: "Approve",
            actionColor: "success",
            callback: (confirm) => handleApprove(confirm)
        });
    };

    const showRetryDialog = () => {
        props.showDialog({
            title: "Retry text translation(s)",
            message: `Are you sure you want to retry the ${textTranslations.selected.length} selected text translation(s)?`,
            actionTitle: "Retry",
            actionColor: "success",
            callback: (confirm) => handleRetry(confirm)
        });
    };

    const handleCreate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form", "text", "_*"]);

        props.showSnackbar({ color: "info", message: "Creating text translation...", duration: 5000 });
        createTextTranslation(props, { body });
    };

    const handleDelete = (confirm) => {
        if (!confirm) return;

        const ids = textTranslations.selected.map((request) => request.id);
        deleteTextTranslations(props, { body: { ids }, ids });
    };

    const handleUpdate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form", "_*", "original_text"]);

        updateTextTranslation(props, { body, id: row.id }); // Using create endpoint for updates as well
    };

    const handleApprove = (confirm) => {
        if (!confirm) return;

        const ids = textTranslations.selected.map((request) => request.id);
        approveTextTranslations(props, { body: { ids } });
    };

    const handleRetry = (confirm) => {
        if (!confirm) return;

        const ids = textTranslations.selected.map((request) => request.id);
        retryTextTranslations(props, { body: { ids } });
    };

    return (
        <PaperFragment
            className="tour-text-translations-2 tour-text-translations-3"
            title={
                <StackedHeader
                    {...props}
                    title="Text translations"
                    subtitle="View and manage text translations."
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected text translations",
                            disabled: textTranslations.selected.length === 0 || textTranslations.selected.some((r) => ["IN_PROGRESS"].includes(r.status)),
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <EditIcon />,
                            tooltip: "Edit selected text translation",
                            onClick: showUpdateDialog,
                            disabled: textTranslations.selected.length !== 1
                        },
                        {
                            icon: <CheckIcon />,
                            tooltip: "Approve selected text translations",
                            onClick: showApproveDialog,
                            disabled: textTranslations.selected.length === 0 || !textTranslations.selected.every((r) => r.status === "AWAITING_APPROVAL")
                        },
                        {
                            icon: <ReplayIcon />,
                            tooltip: "Retry selected text translations",
                            onClick: showRetryDialog,
                            disabled: textTranslations.selected.length === 0 || !textTranslations.selected.every((r) => r.status === "ERROR")
                        }
                    ]}
                    buttons={[
                        {
                            icon: <AddIcon />,
                            title: "New text translation",
                            onClick: showCreateDialog
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultTableOptions}
                    rowHeight={60}
                    states={textTranslations}
                    columns={columns(props)}
                    query={(options) => queryTextTranslations(props, options)}
                    empty={getLanguage().NO_TEXT_TRANSLATIONS_FOUND}
                    refresh={10}
                />
            }
        />
    );
}
